import type { Formatter } from "@kovernow/flows/format";
import { expectedRenewal, summaryRows, type Quote } from "@kovernow/flows/quote";
import { RefreshCw } from "lucide-react";
import { useT } from "../../core/use-t";

/**
 * The selected plan's server amounts: each applied discount, the GST it
 * includes and the total, then "Expected to renew on {date} at {amount}"
 * when the server sent a renewal. Nothing is derived on the client.
 */
export function PriceSummary({ quote, money }: { quote: Quote; money: Formatter }) {
  const t = useT();
  const renewal = expectedRenewal(quote);
  return (
    <section aria-label={t("choosePlan.summary.label")} className="flex flex-col gap-2 rounded-lg bg-muted p-4" data-testid="price-summary">
      <dl className="flex flex-col gap-2">
        {summaryRows(quote).map((row) => (
          <div
            key={row.kind === "discount" ? `discount-${row.code}` : row.kind}
            className={row.kind === "total" ? "flex justify-between gap-4 border-t border-border pt-2 font-semibold" : "flex justify-between gap-4"}
            data-testid={`summary-${row.kind}`}
          >
            <dt>
              {row.kind === "discount"
                ? t("choosePlan.summary.discount", { code: row.code })
                : row.kind === "gst"
                  ? t("choosePlan.summary.gst")
                  : t("choosePlan.summary.total")}
            </dt>
            <dd className="tabular-nums">{row.kind === "discount" ? `−${money.currency(row.amount)}` : money.currency(row.amount)}</dd>
          </div>
        ))}
      </dl>
      {renewal ? (
        <p className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="summary-renewal">
          <RefreshCw aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <span>{t("choosePlan.summary.renewal", { date: money.date(renewal.date), amount: money.currency(renewal.amount) })}</span>
        </p>
      ) : null}
    </section>
  );
}
